import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { SelectOption } from "../../../../../../../types/select";
import { DecimalUtils } from "../../../../../../libs/utils";

type RentTypeValue = "areaSqft" | "fixed";

type UseEscalateRentAfterCopyParams = {
  planIndex: number;
};

export type EscalateRentController = {
  isEscalateAfterCopy: boolean;
  isRentAmountDisabled: boolean;
  handleCopyFromPreviousYearEscalation: () => void;
  handleRentTypeChangeWithEscalationReset: () => void;
  handleEscalateRentPercentChange: (rawPercent: unknown) => void;
};

export const useEscalateRentAfterCopy = (params: UseEscalateRentAfterCopyParams): EscalateRentController => {
  const { planIndex } = params;
  const baseFieldName = `plans[${planIndex}]`;

  const { setValue, getValues } = useFormContext();

  const [isEscalateAfterCopy, setIsEscalateAfterCopy] = useState(false);
  const [escalateCopyKey, setEscalateCopyKey] = useState(0);
  const escalationBaseRef = useRef<{ annualRent: number; rentValue: number } | null>(null);

  useEffect(() => {
    if (!isEscalateAfterCopy) {
      escalationBaseRef.current = null;
      return;
    }

    const annualRent = DecimalUtils.toNumber(getValues(`${baseFieldName}.annualRent`) ?? 0);
    const rentValue = DecimalUtils.toNumber(getValues(`${baseFieldName}.rentValue`) ?? 0);
    escalationBaseRef.current = { annualRent, rentValue };
  }, [baseFieldName, escalateCopyKey, getValues, isEscalateAfterCopy]);

  const handleCopyFromPreviousYearEscalation = useCallback(() => {
    setValue(`${baseFieldName}.escalateRentPercent`, 0, { shouldDirty: true, shouldValidate: false });
    setIsEscalateAfterCopy(true);
    setEscalateCopyKey((k) => k + 1);
  }, [baseFieldName, setValue]);

  const handleRentTypeChangeWithEscalationReset = useCallback(() => {
    setValue(`${baseFieldName}.rentValue`, null);
    setValue(`${baseFieldName}.annualRent`, null);
    setValue(`${baseFieldName}.escalateRentPercent`, 0);
    setIsEscalateAfterCopy(false);
  }, [baseFieldName, setValue]);

  const handleEscalateRentPercentChange = useCallback(
    (rawPercent: unknown) => {
      const base = escalationBaseRef.current;
      if (!base || !isEscalateAfterCopy) return;

      const rentType = (getValues(`${baseFieldName}.rentType`) as SelectOption<RentTypeValue> | undefined)?.value;
      const escalationMultiplier = DecimalUtils.toNumber(
        DecimalUtils.add([1, DecimalUtils.multiply((rawPercent ?? 0) as string | number, 0.01)]),
      );

      if (rentType === "areaSqft") {
        const updatedRentValue = DecimalUtils.toNumber(DecimalUtils.multiply(base.rentValue, escalationMultiplier));
        setValue(`${baseFieldName}.rentValue`, updatedRentValue, { shouldDirty: true, shouldValidate: true });

        const area = getValues(`${baseFieldName}.unitAreaInSqft`) ?? 0;
        if (area) {
          setValue(
            `${baseFieldName}.annualRent`,
            String(DecimalUtils.toNumber(DecimalUtils.multiply(updatedRentValue, area))),
            { shouldDirty: true, shouldValidate: true },
          );
        }
        return;
      }

      setValue(
        `${baseFieldName}.annualRent`,
        DecimalUtils.toNumber(DecimalUtils.multiply(base.annualRent, escalationMultiplier)),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    },
    [baseFieldName, getValues, isEscalateAfterCopy, setValue],
  );

  return {
    isEscalateAfterCopy,
    isRentAmountDisabled: isEscalateAfterCopy,
    handleCopyFromPreviousYearEscalation,
    handleRentTypeChangeWithEscalationReset,
    handleEscalateRentPercentChange,
  };
};
